import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { SlotsNGController as SlotController } from '../../controllers/slots-ng-controller';
import { ResizeController } from '../../controllers/resize-controller';
import { screenSizeMap } from '../../controllers/screen-size-controller';
import '../sic-container/sic-container';
import { backgroundColor } from '../sic-container/sic-container';

import componentStyles from './sic-promo.css.lit';
import defaultVars from './css-variables/sic-promo-vars.css.lit';

/**
 * The Promo component.
 * @element sic-promo
 * @slot heading
 * @slot body
 * @slot cta (cannot go into the shadow dom - a11y)
 * @slot image
 *
 * @attr media-first
 * @attr background-color
 *
 * Lightdom styles in sic-promo.lightdom.css included in /shared.css
 */

@customElement('sic-promo')
export class SicPromo extends OutlineElement {
  private slots = new SlotController(this);
  static styles: CSSResultGroup = [componentStyles, defaultVars];
  resizeController: ResizeController;
  // The resizeController is initialized here because visibleBreakpoint is not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    this.resizeController = new ResizeController(this, {
      breakpoints: [screenSizeMap.get(this.visibleBreakpoint) as number],
      containerQuery: false,
    });
  }

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'media-first',
  })
  mediaFirst: false;

  @property({
    type: String,
    reflect: true,
    attribute: 'background-color',
  })
  backgroundColor: backgroundColor = 'primary-default';

  /**
   * The breakpoint at which the switch from mobile to desktop layout.
   *
   * This is done as a variable to ensure flexibility for future usages.
   */
  @property({ type: String, attribute: 'breakpoint' })
  visibleBreakpoint = 'md';

  @state() private isStacked = false;

  render(): TemplateResult {
    this.isStacked = this.resizeController.currentBreakpointRange === 0;
    const wrapperClasses = {
      'promo-wrapper': true,
      'layout-stacked': this.isStacked,
      'layout-media-first': this.mediaFirst,
    };

    // stacked view
    if (this.isStacked || this.mediaFirst) {
      return html`
        <sic-container
          background-color="${this.backgroundColor}"
          container-width="full"
          top-padding="none"
          bottom-padding="none"
        >
          <div class="${classMap(wrapperClasses)}">
            ${this.mediaTemplate()} ${this.contentTemplate()}
          </div>
        </sic-container>
      `;
    } else {
      return html`
        <sic-container
          background-color="${this.backgroundColor}"
          container-width="full"
          top-padding="none"
          bottom-padding="none"
        >
          <div class="${classMap(wrapperClasses)}">
            ${this.contentTemplate()} ${this.mediaTemplate()}
          </div>
        </sic-container>
      `;
    }
  }

  contentTemplate(): TemplateResult {
    return html` <div class="content-wrapper">
      <div class="content">
        ${this.headingTemplate()} ${this.slots.conditionalSlot('body', false)}
        ${this.slots.conditionalSlot('cta', false)}
      </div>
    </div>`;
  }

  mediaTemplate(): TemplateResult {
    return html`
      <div class="promo-image">
        ${this.slots.conditionalSlot('image', false)}
      </div>
    `;
  }
  headingTemplate(): TemplateResult {
    if (this.slots.slotExists('heading')) {
      return html` <sic-heading level-size="6" class="promo-heading">
        ${this.slots.conditionalSlot('heading')}
      </sic-heading>`;
    }
    return html``;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-promo': SicPromo;
  }
}
